import React, { memo, useCallback, useMemo } from 'react';
import PropTypes from 'prop-types';
import classNames from 'classnames';
import { createUseStyles, useTheme, Icon, Modal, Text } from '@Sage/hoi-poi-ui';
import Illustrations from './components/Illustrations';

import styles from './styles/ModalContactRequest';

const { ContactRequest } = Illustrations;
const useStyles = createUseStyles(styles, 'ModalContactRequest');

const ModalContactRequest = memo(({ content, customImage, ...props }) => {
    const classes = useStyles();
    const theme = useTheme();

    const modalProps = useMemo(() => {
        return {
            size: 'small',
            useAutoHeight: false,
            ...props,
        };
    }, [props]);

    const Img = useMemo(() => customImage || ContactRequest, [customImage]);

    const renderList = useCallback(
        (content, key) => (
            <ol className={classes.list} key={key}>
                {content.map((item, index) => (
                    <li
                        className={classNames(classes.listItem, {
                            [classes.listItemActive]: item.active,
                        })}
                        key={`${key}-${index}`}
                    >
                        {item.active ? (
                            <Icon
                                color={theme.colors.semantic.positive500}
                                name="taskChecked"
                                size="large"
                                className={classes.bullet}
                            />
                        ) : (
                            <Text
                                color={theme.colors.textLight.secondary}
                                className={classNames(classes.bullet, classes.bulletNumber)}
                            >
                                {index + 1}
                            </Text>
                        )}
                        <Text
                            color={
                                item.active
                                    ? theme.colors.textLight.primary
                                    : theme.colors.textLight.secondary
                            }
                        >
                            {item.text}
                        </Text>
                    </li>
                ))}
            </ol>
        ),
        [
            classes.bullet,
            classes.bulletNumber,
            classes.list,
            classes.listItem,
            classes.listItemActive,
            theme,
        ],
    );

    const renderContent = useMemo(() => {
        return content.reduce((arr, item, index) => {
            let newContent;
            switch (item.type) {
                case 'text':
                    newContent = <Text key={index}>{item.content}</Text>;
                    break;
                case 'list':
                    if (!item.content.length) return arr;
                    newContent = renderList(item.content, index);
                    break;
                default:
                    return arr;
            }
            return [...arr, newContent];
        }, []);
    }, [content, renderList]);

    return (
        <Modal {...modalProps}>
            <figure className={classes.figure}>
                <Img />
            </figure>
            {renderContent && <div className={classes.content}>{renderContent}</div>}
        </Modal>
    );
});

ModalContactRequest.propTypes = {
    content: PropTypes.arrayOf(
        PropTypes.shape({
            type: PropTypes.oneOf(['list', 'text']),
            content: PropTypes.oneOfType([
                PropTypes.string,
                PropTypes.arrayOf(
                    PropTypes.shape({
                        text: PropTypes.string,
                        active: PropTypes.bool,
                    }),
                ),
            ]),
        }),
    ),
    customImage: PropTypes.node,
    videoSrc: PropTypes.string,
    isOpen: PropTypes.bool,
    title: PropTypes.string,
    confirmText: PropTypes.string,
    onConfirm: PropTypes.func,
};

export default ModalContactRequest;
